import React from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";

const SITE_LINKS = [
  { label: "Websites", to: "/websites" },
  { label: "Revenue Systems", to: "/revenue-systems" },
  { label: "Packages", to: "/packages" },
  { label: "Insights", to: "/insights" },
  { label: "FAQ", to: "/faq" },
];

const SOCIALS = [
  {
    Icon: Instagram,
    label: "Instagram",
    handle: "@themdrnext",
    href: "https://www.instagram.com/themdrnext",
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-ink-deep text-sand-300 px-5 sm:px-6 md:px-10 pb-10 pt-12 sm:pt-16"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-8 sm:gap-10 pb-10 border-b border-white/8">
          <div className="sm:col-span-2 lg:col-span-6">
            <Link to="/" className="inline-block">
              <p className="font-display text-xl sm:text-2xl md:text-3xl text-sand-100 font-semibold tracking-tight">
                The Modern Exit
              </p>
            </Link>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
              Funnels to Freedom
            </p>
            <p className="mt-5 max-w-md text-[14px] leading-relaxed text-sand-300/85">
              Two divisions. One studio. We build clean websites for service
              businesses — and the CRM, follow-up, booking, payment, and
              automation systems that help leads turn into booked revenue.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map(({ Icon, label, handle, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${label} — ${handle}`}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="group inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 pl-3 pr-4 h-10 text-sand-100 hover:bg-white/10 hover:border-clay/40 transition-colors"
                >
                  <Icon size={15} strokeWidth={1.6} />
                  <span className="text-[13px] font-medium">{handle}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="sm:col-span-1 lg:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand-300/70">
              Site
            </p>
            <ul className="mt-4 space-y-2.5">
              {SITE_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[14px] text-sand-100 hover:text-clay transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="sm:col-span-1 lg:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand-300/70">
              Contact
            </p>
            <ul className="mt-4 space-y-2.5 text-[14px] text-sand-300/85">
              <li data-testid="footer-email">
                <a href="mailto:info@themodernexit.net" className="hover:text-clay transition-colors">
                  info@themodernexit.net
                </a>
              </li>
              <li data-testid="footer-phone">
                <a href="tel:+12142539779" className="hover:text-clay transition-colors">
                  +1 (214) 253-9779
                </a>
              </li>
              <li data-testid="footer-handle">
                <a
                  href="https://www.instagram.com/themdrnext"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-clay transition-colors"
                >
                  @themdrnext
                </a>
              </li>
              {/* "Book a call" intentionally removed from the public footer —
                  /book is post-intake-only and exposed via IntakePage success card. */}
            </ul>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[12px] text-sand-300/60">
          <p>© {new Date().getFullYear()} The Modern Exit. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="hover:text-clay transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-link-terms"
              className="hover:text-clay transition-colors"
            >
              Terms
            </Link>
            <p className="font-mono uppercase tracking-[0.2em]">
              · websites · revenue systems ·
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
